import {
  BUILDING_TILE_KINDS,
  UNIT_KINDS,
  type BuildingTileKind,
  type PlayerId,
  type UnitKind,
} from "@dailywars/core/engine";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "#/components/ui/accordion";
import { cn } from "#/lib/utils";

import {
  PROFILE_UNITS_KILLED_KINDS,
  RecordBreakdown,
  StatRowStripe,
  buildingBreakdownEntries,
  unitBreakdownEntries,
} from "./-profile.breakdownRows";

type StatsBreakdownSectionUnits = {
  value: string;
  label: string;
  total: number;
  breakdown: "units";
  record: Partial<Record<UnitKind, number>>;
};

type StatsBreakdownSectionBuildings = {
  value: string;
  label: string;
  total: number;
  breakdown: "buildings";
  record: Partial<Record<BuildingTileKind, number>>;
};

export type StatsBreakdownSection = StatsBreakdownSectionUnits | StatsBreakdownSectionBuildings;

function breakdownRowCount(section: StatsBreakdownSection): number {
  if (section.breakdown === "buildings") return BUILDING_TILE_KINDS.length;
  return section.value === "units-killed" ? PROFILE_UNITS_KILLED_KINDS.length : UNIT_KINDS.length;
}

function accordionTriggerStripeIndex({
  sectionIndex,
  simpleRowCount,
  sections,
  openValues,
}: {
  sectionIndex: number;
  simpleRowCount: number;
  sections: readonly StatsBreakdownSection[];
  openValues: readonly string[];
}): number {
  let row = simpleRowCount;
  for (let j = 0; j < sectionIndex; j++) {
    row += 1;
    if (openValues.includes(sections[j].value)) {
      row += breakdownRowCount(sections[j]);
    }
  }
  return row;
}

export function StatsPrimaryRows({ items }: { items: Array<{ label: string; value: number }> }) {
  return (
    <>
      {items.map((item, index) => (
        <StatRowStripe key={item.label} index={index}>
          <span className="text-sm text-muted-foreground">{item.label}</span>
          <span className="font-mono text-sm font-semibold tabular-nums text-foreground">
            {item.value.toLocaleString()}
          </span>
        </StatRowStripe>
      ))}
    </>
  );
}

function sectionUnitOwner(section: StatsBreakdownSection): PlayerId {
  if (section.breakdown !== "units") return "p1";
  return section.value === "units-killed" ? "p2" : "p1";
}

function sectionBreakdownEntries(section: StatsBreakdownSection) {
  if (section.breakdown === "buildings") return buildingBreakdownEntries(section.record);
  const kinds = section.value === "units-killed" ? PROFILE_UNITS_KILLED_KINDS : UNIT_KINDS;
  return unitBreakdownEntries(section.record, kinds);
}

function StatsBreakdownSectionItem({
  section,
  sectionIndex,
  simpleRowCount,
  sections,
  openValues,
}: {
  section: StatsBreakdownSection;
  sectionIndex: number;
  simpleRowCount: number;
  sections: readonly StatsBreakdownSection[];
  openValues: readonly string[];
}) {
  const stripeIndex = accordionTriggerStripeIndex({
    sectionIndex,
    simpleRowCount,
    sections,
    openValues,
  });
  return (
    <AccordionItem value={section.value} className="border-b-0!">
      <AccordionTrigger
        className={cn(
          "items-center rounded-none px-3 py-3! hover:no-underline",
          stripeIndex % 2 === 1 && "bg-muted",
        )}
      >
        <span className="flex flex-1 items-center justify-between gap-4 pr-2">
          <span className="text-sm text-muted-foreground">{section.label}</span>
          <span className="font-mono text-sm font-semibold tabular-nums text-foreground">
            {section.total.toLocaleString()}
          </span>
        </span>
      </AccordionTrigger>
      <AccordionContent className="px-0 pb-0">
        <RecordBreakdown
          stripeStartIndex={stripeIndex}
          rowKind={section.breakdown === "units" ? "unit" : "building"}
          unitOwner={sectionUnitOwner(section)}
          buildingOwner="p1"
          entries={sectionBreakdownEntries(section)}
        />
      </AccordionContent>
    </AccordionItem>
  );
}

export function StatsBreakdownAccordion({
  openValues,
  onOpenChange,
  simpleRowCount,
  sections,
}: {
  openValues: string[];
  onOpenChange: (values: string[]) => void;
  simpleRowCount: number;
  sections: readonly StatsBreakdownSection[];
}) {
  return (
    <Accordion className="w-full" value={openValues} onValueChange={onOpenChange}>
      {sections.map((section, i) => (
        <StatsBreakdownSectionItem
          key={section.value}
          section={section}
          sectionIndex={i}
          simpleRowCount={simpleRowCount}
          sections={sections}
          openValues={openValues}
        />
      ))}
    </Accordion>
  );
}
